/**
 * BucketCharts — interactive right-pane visualizations for an expanded
 * Pricing Breakdown card on /cpt/xxx /hcpcs/xxx /drg/xxx pages.
 *
 * Three views, all driving the same per-bucket filter state held by the parent:
 *   1. US state choropleth — colors states by median price for this code in that state.
 *      Click a state → filters left-pane table.
 *   2. Price vs Revenue-per-bed scatter — each dot = one hospital. X axis togglable
 *      between revenue/bed and CEO comp. Tells the "do the wealthiest hospitals
 *      charge the most?" story.
 *   3. Top 10 hospital systems (parent corporate networks like HCA, Tenet, Kaiser).
 *      Click a system → filters left-pane table.
 *
 * Data sources:
 *   - hospitals: deduped list from the parent (gross_charge per hospital + state)
 *   - financeMap: bulk-fetched at the page level (bed_count, total_revenue, ceo_comp, system)
 *
 * The CEO comp toggle is gated on coverage — currently only ~9 hospitals have 990 exec
 * data extracted, so the toggle is disabled until at least 30 dots are available, with
 * a tooltip explaining why. Bulk EIN extraction is task #83.
 */
import { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis } from 'recharts';
import { stateAbbreviations } from '@/components/USAMapNavigator';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

interface HospitalLike {
  hospital_id: string;
  hospital_name: string;
  city: string;
  state: string;
  gross_charge: number;
}
interface FinanceEntry {
  bed_count?: number;
  total_revenue?: number;
  system?: string;
  ceo_comp?: number;
}
interface ThemeTokens {
  cardBg: string;
  cardBorder: string;
  cardShadow: string;
  groupHeader: string;
  nameText: string;
  mutedText: string;
  fadedText: string;
  divider: string;
  pillTopBg: string;
  pillTopBd: string;
  pillTopTx: string;
}

interface BucketChartsProps {
  bucketKey: string;
  hospitals: HospitalLike[];
  financeMap?: Map<string, FinanceEntry>;
  filterState?: { type: 'state' | 'system'; value: string };
  setBucketFilter: (f: { type: 'state' | 'system'; value: string } | undefined) => void;
  isAhp: boolean;
  isDark: boolean;
  t_: ThemeTokens;
}

export function BucketCharts({
  bucketKey,
  hospitals,
  financeMap,
  filterState,
  setBucketFilter,
  isAhp,
  isDark,
  t_,
}: BucketChartsProps) {
  // Toggle for the scatter X axis. When CEO comp coverage is too sparse the toggle is
  // disabled (see note at top of file).
  const [scatterXMode, setScatterXMode] = useState<'revPerBed' | 'ceoComp'>('revPerBed');

  // ── State medians ────────────────────────────────────────────────────────
  const stateData = useMemo(() => {
    const m = new Map<string, number[]>();
    for (const h of hospitals) {
      if (!h.state) continue;
      const s = h.state.toUpperCase();
      const arr = m.get(s) || [];
      arr.push(h.gross_charge);
      m.set(s, arr);
    }
    const out: Record<string, { median: number; count: number; min: number; max: number }> = {};
    for (const [state, prices] of m.entries()) {
      const sorted = [...prices].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      out[state] = { median, count: prices.length, min: sorted[0], max: sorted[sorted.length - 1] };
    }
    return out;
  }, [hospitals]);

  const stateValues = Object.values(stateData).map(s => s.median);
  const stateMinMedian = stateValues.length ? Math.min(...stateValues) : 0;
  const stateMaxMedian = stateValues.length ? Math.max(...stateValues) : 1;

  // ── Scatter data: hospital × (X axis metric) × Y price ──────────────────
  const scatterData = useMemo(() => {
    if (!financeMap) return [] as Array<{ name: string; x: number; y: number; bed_count: number; hospital_id: string; state: string; system: string | null }>;
    const out: Array<{ name: string; x: number; y: number; bed_count: number; hospital_id: string; state: string; system: string | null }> = [];
    for (const h of hospitals) {
      const fin = financeMap.get(h.hospital_id);
      if (!fin) continue;
      let x: number | undefined;
      if (scatterXMode === 'revPerBed') {
        if (fin.total_revenue && fin.bed_count && fin.bed_count > 0) {
          x = fin.total_revenue / fin.bed_count;
        }
      } else if (scatterXMode === 'ceoComp') {
        x = fin.ceo_comp;
      }
      if (x == null || !isFinite(x) || x <= 0) continue;
      out.push({
        name: h.hospital_name,
        x,
        y: h.gross_charge,
        bed_count: fin.bed_count || 100,
        hospital_id: h.hospital_id,
        state: h.state,
        system: fin.system || null,
      });
    }
    return out;
  }, [hospitals, financeMap, scatterXMode]);

  const ceoCompCoverage = useMemo(() => {
    if (!financeMap) return 0;
    let n = 0;
    for (const h of hospitals) {
      if (financeMap.get(h.hospital_id)?.ceo_comp) n++;
    }
    return n;
  }, [hospitals, financeMap]);
  const ceoToggleAvailable = ceoCompCoverage >= 5;

  // ── Top 10 hospital systems ──────────────────────────────────────────────
  const systemRows = useMemo(() => {
    if (!financeMap) return [] as Array<{ system: string; count: number; avg: number; median: number }>;
    const groups = new Map<string, number[]>();
    for (const h of hospitals) {
      const fin = financeMap.get(h.hospital_id);
      const sys = fin?.system?.trim();
      if (!sys) continue;
      const arr = groups.get(sys) || [];
      arr.push(h.gross_charge);
      groups.set(sys, arr);
    }
    const rows: Array<{ system: string; count: number; avg: number; median: number }> = [];
    for (const [sys, prices] of groups.entries()) {
      if (prices.length < 2) continue; // single-hospital systems aren't really "systems"
      const sorted = [...prices].sort((a, b) => a - b);
      const avg = prices.reduce((s, p) => s + p, 0) / prices.length;
      rows.push({ system: sys, count: prices.length, avg, median: sorted[Math.floor(sorted.length / 2)] });
    }
    return rows.sort((a, b) => b.count - a.count).slice(0, 10);
  }, [hospitals, financeMap]);

  // ── Color helpers ────────────────────────────────────────────────────────
  // Choropleth: cream (low) → AHP gold → AHP red (high)
  const choroplethColor = (value: number): string => {
    if (stateMaxMedian === stateMinMedian) return '#C5A55A';
    const t = (value - stateMinMedian) / (stateMaxMedian - stateMinMedian);
    if (t < 0.5) {
      // cream → gold
      const r = Math.round(245 - (245 - 197) * (t * 2));
      const g = Math.round(235 - (235 - 165) * (t * 2));
      const b = Math.round(215 - (215 - 90) * (t * 2));
      return `rgb(${r},${g},${b})`;
    }
    // gold → AHP red
    const t2 = (t - 0.5) * 2;
    const r = Math.round(197 + (178 - 197) * t2);
    const g = Math.round(165 + (34 - 165) * t2);
    const b = Math.round(90 + (52 - 90) * t2);
    return `rgb(${r},${g},${b})`;
  };
  const noDataColor = isDark ? '#1a2a44' : '#e5e5e5';

  const fmtUsd = (v: number) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${Math.round(v)}`;

  return (
    <div className="space-y-5" onClick={(e) => e.stopPropagation()}>

      {/* Active filter chip */}
      {filterState && (
        <div className="flex items-center gap-2 text-[11px]" style={{ color: t_.mutedText }}>
          <span className="font-semibold uppercase tracking-wider">Filter:</span>
          <span className="px-2 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(197,165,90,0.18)', color: '#7A5F1F', border: '1px solid rgba(197,165,90,0.5)' }}>
            {filterState.type === 'state' ? `state: ${filterState.value}` : `system: ${filterState.value}`}
          </span>
          <button
            type="button"
            onClick={() => setBucketFilter(undefined)}
            className="underline opacity-80 hover:opacity-100"
          >
            clear
          </button>
        </div>
      )}

      {/* ── 1. US state choropleth ──────────────────────────────────────── */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: t_.mutedText }}>
            Median price by state · click to filter
          </span>
          {stateValues.length > 0 && (
            <span className="text-[10px] tabular-nums" style={{ color: t_.fadedText }}>
              range: {fmtUsd(stateMinMedian)} – {fmtUsd(stateMaxMedian)}
            </span>
          )}
        </div>
        <ComposableMap projection="geoAlbersUsa" className="w-full h-auto" style={{ maxHeight: 220 }}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const stateName = geo.properties.name;
                const code = stateAbbreviations[stateName];
                if (!code) return null;
                const sd = stateData[code];
                const fill = sd ? choroplethColor(sd.median) : noDataColor;
                const isSelected = filterState?.type === 'state' && filterState.value === code;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => sd && setBucketFilter(isSelected ? undefined : { type: 'state', value: code })}
                    style={{
                      default: {
                        fill,
                        stroke: isSelected ? '#001238' : (isDark ? 'rgba(245,235,215,0.20)' : 'rgba(0,18,56,0.25)'),
                        strokeWidth: isSelected ? 2 : 0.5,
                        outline: 'none',
                        cursor: sd ? 'pointer' : 'default',
                        opacity: sd ? 1 : 0.4,
                        transition: 'all 0.15s',
                      },
                      hover: {
                        fill,
                        stroke: '#001238',
                        strokeWidth: 1.5,
                        outline: 'none',
                        filter: 'brightness(1.10)',
                        cursor: sd ? 'pointer' : 'default',
                      },
                      pressed: { fill, stroke: '#001238', strokeWidth: 2, outline: 'none' },
                    }}
                  >
                    <title>
                      {sd ? `${code}: median ${fmtUsd(sd.median)} across ${sd.count} hospitals` : `${code}: no data`}
                    </title>
                  </Geography>
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {/* ── 2. Scatter: price vs revenue-per-bed (or CEO comp) ─────────── */}
      <div>
        <div className="flex items-center justify-between mb-1.5 flex-wrap gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: t_.mutedText }}>
            Price vs hospital wealth · {scatterData.length} hospitals plotted
          </span>
          <div className="flex gap-1 text-[10px]">
            <button
              type="button"
              onClick={() => setScatterXMode('revPerBed')}
              className="px-2 py-0.5 rounded-sm border"
              style={{
                background: scatterXMode === 'revPerBed' ? 'rgba(197,165,90,0.20)' : 'transparent',
                borderColor: scatterXMode === 'revPerBed' ? '#C5A55A' : 'rgba(0,18,56,0.20)',
                color: scatterXMode === 'revPerBed' ? '#7A5F1F' : t_.mutedText,
              }}
            >
              Revenue / bed
            </button>
            <button
              type="button"
              disabled={!ceoToggleAvailable}
              onClick={() => setScatterXMode('ceoComp')}
              className="px-2 py-0.5 rounded-sm border"
              title={ceoToggleAvailable
                ? 'X axis = highest-paid executive total comp'
                : `CEO comp data only available for ${ceoCompCoverage} of ${hospitals.length} hospitals — bulk extraction in progress`}
              style={{
                background: scatterXMode === 'ceoComp' ? 'rgba(197,165,90,0.20)' : 'transparent',
                borderColor: scatterXMode === 'ceoComp' ? '#C5A55A' : 'rgba(0,18,56,0.20)',
                color: scatterXMode === 'ceoComp' ? '#7A5F1F' : t_.mutedText,
                opacity: ceoToggleAvailable ? 1 : 0.4,
                cursor: ceoToggleAvailable ? 'pointer' : 'not-allowed',
              }}
            >
              CEO comp{!ceoToggleAvailable && ` (${ceoCompCoverage})`}
            </button>
          </div>
        </div>
        {scatterData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 8, right: 12, left: 4, bottom: 28 }}>
              <CartesianGrid stroke={isDark ? 'rgba(245,235,215,0.10)' : 'rgba(0,18,56,0.08)'} />
              <XAxis
                type="number"
                dataKey="x"
                tickFormatter={(v) => fmtUsd(v)}
                stroke={isDark ? 'rgba(245,235,215,0.55)' : 'rgba(0,18,56,0.55)'}
                tick={{ fill: isDark ? 'rgba(245,235,215,0.65)' : 'rgba(0,18,56,0.65)', fontSize: 10 }}
                label={{
                  value: scatterXMode === 'revPerBed' ? 'Total revenue per staffed bed' : 'Highest-paid exec total comp',
                  position: 'insideBottom',
                  offset: -16,
                  style: { fill: isDark ? 'rgba(245,235,215,0.55)' : 'rgba(0,18,56,0.55)', fontSize: 10 },
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                tickFormatter={(v) => fmtUsd(v)}
                stroke={isDark ? 'rgba(245,235,215,0.55)' : 'rgba(0,18,56,0.55)'}
                tick={{ fill: isDark ? 'rgba(245,235,215,0.65)' : 'rgba(0,18,56,0.65)', fontSize: 10 }}
                label={{ value: 'Price for this code', angle: -90, position: 'insideLeft', style: { fill: isDark ? 'rgba(245,235,215,0.55)' : 'rgba(0,18,56,0.55)', fontSize: 10 } }}
              />
              <ZAxis type="number" dataKey="bed_count" range={[30, 250]} name="beds" />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{
                  backgroundColor: isDark ? 'rgba(2, 22, 60, 0.95)' : '#FBF7EE',
                  border: `1px solid ${isDark ? 'rgba(197, 165, 90, 0.45)' : 'rgba(0, 18, 56, 0.20)'}`,
                  borderRadius: 6,
                  color: isDark ? '#F5EBD7' : '#001238',
                  fontSize: 11,
                }}
                formatter={(value: any, name: string) => {
                  if (name === 'beds') return [`${value} beds`, 'Hospital size'];
                  return [fmtUsd(Number(value)), name === 'x' ? (scatterXMode === 'revPerBed' ? 'Revenue/bed' : 'CEO comp') : 'Price'];
                }}
                labelFormatter={() => ''}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload;
                  return (
                    <div className="rounded-md p-2.5 text-[11px] shadow-lg" style={{
                      backgroundColor: isDark ? 'rgba(2, 22, 60, 0.95)' : '#FBF7EE',
                      border: `1px solid ${isDark ? 'rgba(197, 165, 90, 0.45)' : 'rgba(0, 18, 56, 0.20)'}`,
                      color: isDark ? '#F5EBD7' : '#001238',
                    }}>
                      <div className="font-semibold mb-1">{p.name}</div>
                      <div className="opacity-80">{p.state} · {p.bed_count} beds{p.system ? ` · ${p.system}` : ''}</div>
                      <div className="mt-1 grid grid-cols-2 gap-x-3">
                        <span className="opacity-60">{scatterXMode === 'revPerBed' ? 'Rev/bed' : 'CEO comp'}:</span>
                        <span className="font-mono font-bold">{fmtUsd(p.x)}</span>
                        <span className="opacity-60">Price:</span>
                        <span className="font-mono font-bold">{fmtUsd(p.y)}</span>
                      </div>
                    </div>
                  );
                }}
              />
              <Scatter
                data={scatterData}
                fill={isAhp ? '#B22234' : '#10b981'}
                fillOpacity={0.55}
                stroke={isAhp ? '#9A1E2D' : '#0e9670'}
                strokeWidth={1}
              />
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-[11px] text-center py-6" style={{ color: t_.fadedText }}>
            {financeMap == null
              ? 'Loading hospital finance data…'
              : `No matching hospitals have both ${scatterXMode === 'revPerBed' ? 'revenue + bed count' : 'CEO comp'} data on file.`}
          </div>
        )}
        {scatterXMode === 'revPerBed' && (
          <div className="text-[10px] mt-1 italic" style={{ color: t_.fadedText }}>
            Revenue/bed is a "wealth intensity" proxy. Each dot is a hospital; size = bed count.
            A rising trend would mean the wealthiest hospitals also charge the most for this code.
          </div>
        )}
      </div>

      {/* ── 3. Top 10 hospital systems ─────────────────────────────────── */}
      {systemRows.length > 0 && (
        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider" style={{ color: t_.mutedText }}>
            Top hospital systems · click to filter
          </div>
          <div className="space-y-1">
            {systemRows.map((sr) => {
              const maxAvg = systemRows[0].avg > systemRows[0].median ? Math.max(...systemRows.map(x => x.avg)) : Math.max(...systemRows.map(x => x.median));
              const widthPct = (sr.avg / maxAvg) * 100;
              const isSelected = filterState?.type === 'system' && filterState.value === sr.system;
              return (
                <button
                  key={sr.system}
                  type="button"
                  onClick={() => setBucketFilter(isSelected ? undefined : { type: 'system', value: sr.system })}
                  className="w-full flex items-center gap-2 text-[11px] hover:opacity-80 transition-opacity"
                  title={`${sr.system}: ${sr.count} facilities · avg ${fmtUsd(sr.avg)} · median ${fmtUsd(sr.median)}`}
                >
                  <span className="font-semibold w-32 sm:w-40 text-left truncate" style={{ color: t_.nameText }}>
                    {sr.system}
                  </span>
                  <div className="flex-1 h-4 rounded-sm relative overflow-hidden" style={{ background: isDark ? 'rgba(245,235,215,0.04)' : 'rgba(0,18,56,0.04)' }}>
                    <div
                      className="h-full rounded-sm transition-all"
                      style={{
                        width: `${widthPct}%`,
                        background: isSelected ? '#C5A55A' : (isDark ? 'rgba(178,34,52,0.55)' : 'rgba(178,34,52,0.65)'),
                        border: isSelected ? '1.5px solid #C5A55A' : 'none',
                      }}
                    />
                    <span className="absolute inset-0 flex items-center justify-end pr-1.5 font-mono tabular-nums text-[10px]" style={{ color: t_.nameText }}>
                      {fmtUsd(sr.avg)}
                    </span>
                  </div>
                  <span className="font-mono tabular-nums w-10 text-right opacity-70" style={{ color: t_.fadedText }}>
                    {sr.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
